import AccountLsdModel             from './AccountLsdModel';
import UserModel                   from './fondationsModels/UserModel';
import axios                       from 'axios';
import CompanyCookie               from './CompanyCookie';
import ConfigProxy                 from '../tools/ConfigProxy';
import currentModelXMercureService from '../currentModelXMercureService';
import GroupLsdModel               from './GroupLsdModel';
import i18n                        from 'components/i18n';
import jwtDecode                   from 'jwt-decode';
import MeModel                     from './MeModel';
import MercureTokenModel           from './MercureTokenModel';
import notification                from 'antd/lib/notification';
import qs                          from 'qs';
import Raven                       from 'raven-js';
import SessionUser                 from './SessionUser';
import store                       from '../store';
import { action }                  from 'mobx';
import { computed }                from 'mobx';
import { observable }              from 'mobx';
import { ApiCollection }           from 'modelx-jts-common';
import { getIriFromId }            from 'tools/lsdTools';
import { getSharedItem }            from 'tools/sharedAuthCookie';
import { setSharedItem }            from 'tools/sharedAuthCookie';
import { SHARED_REFRESH_TOKEN_KEY } from 'tools/sharedAuthCookie';
import { SHARED_TOKEN_KEY }         from 'tools/sharedAuthCookie';
import { Model }                   from '@mathquis/modelx';
import { Platform }                from 'proto/Service/Fondations/UserRights_pb';
import UserRights = Platform.Service.Fondations.UserRights;

const WITH_REDIRECTION_TO_COMPANY = true;
const WITHOUT_REDIRECTION_TO_COMPANY = false;

// Clé de l'ancienne session stockée en localStorage (avant le passage aux cookies partagés)
const LEGACY_SESSION_USER_KEY = '/user/current';

// import { Platform } from 'proto/proto';
// import UserRights = Platform.Service.Fondations.UserRights;

// import * as Proto from 'proto/Service/Fondations/UserRights_pb';
// const UserRights = Proto.UserRights;

const client = axios.create({
	baseURL: ConfigProxy.getServiceConfig('fondations', 'api_endpoint'),
	headers: {
		'Accept': 'application/json',
		'Content-Type': 'application/x-www-form-urlencoded'
	},
	responseType: 'json'
});

export default class Session extends Model {

	public accountModel = new UserModel();
	public companyCookie = new CompanyCookie();
	public lsdGroups = new ApiCollection(GroupLsdModel);
	public me = new MeModel();
	public protoUserRights = new UserRights();
	public user = new SessionUser();

	@observable
	public _loggedIn = false;

	@observable
	public _processing = true;

	@observable
	public _refreshing = false;

	@computed
	public get isProcessing(): boolean {
		return this._processing;
	}

	@computed
	public get isRefreshing(): boolean {
		return this._refreshing;
	}

	@computed
	public get isLoggedIn(): boolean {
		return this._loggedIn;
	}

	@computed
	public get isExpired(): boolean {
		return this.user.get('user_data.exp', 0) < Math.ceil(new Date().valueOf() / 1000);
	}

	@computed
	public get isAdmin(): boolean {
		return !!this.user && this.user.isAdmin();
	}

	@computed
	public get isManager(): boolean {
		return !!this.user && this.user.isManager();
	}

	@computed
	public get isSuperAdmin(): boolean {
		return !!this.user && this.user.isSuperAdmin();
	}

	@computed
	public get userId() {
		return this.accountModel.id;
	}

	@computed
	public get userIri() {
		const currentUserId = this.accountModel.id;
		return getIriFromId(AccountLsdModel, currentUserId);
	}

	public async login(username: string, password: string) {
		this.setIsProcessing(true);

		// Pas de save : un échec de connexion ne doit pas supprimer la session partagée des autres applications
		this.user
			.set({
				id: 'current',
				user_data: {
					user_informations: {
						avatar: '',
						firstName: '',
						lastName: ''
					}
				},
				refreshToken: '',
				token: '',
			});

		return client('/login_check', {
			method: 'post',
			data: qs.stringify({
				_username: username,
				_password: password,
				from: 1 // TODO: Use the correct value otherwise it does not work if missing
			})
		})
			.then((response) => {
				return this.onLoggedIn(response, WITH_REDIRECTION_TO_COMPANY);
			})
			.finally(
				() => this.setIsProcessing(false)
			);
	}

	public refresh(): Promise<void> | void {
		// Pas de session LSD en cours (ex : session partagée refusée au démarrage) : rien à renouveler
		if (this._refreshing || !this.token) {
			return;
		}

		const sharedToken = getSharedItem(SHARED_TOKEN_KEY);
		const sharedRefreshToken = getSharedItem(SHARED_REFRESH_TOKEN_KEY);

		// Déconnecté depuis une autre application
		if (!sharedToken && !sharedRefreshToken) {
			this._clearLocalSession();

			return;
		}

		this._refreshing = true;

		// Une autre application a déjà renouvelé le token
		const promise: Promise<any> = sharedToken && sharedToken !== this.token && this._isTokenValid(sharedToken)
			? this.onLoggedIn({ data: { token: sharedToken, refresh_token: sharedRefreshToken } })
			: this._refreshWithToken(sharedRefreshToken || this.user.get('refreshToken'));

		return promise
			.then(() => {
				// nothing
			})
			.catch(() => {
				this._refreshing = false;

				// Ne supprime pas les cookies partagés : une autre application a pu renouveler le token entre-temps
				this._clearLocalSession();
			});
	}

	public realLogout() {
		// Supprime les cookies partagés : déconnecte aussi les autres applications
		this.user.destroy();

		this._clearLocalSession();
	}

	public logout() {
		// call each time there is a 401
		// whereas we want it to be call only when refresh fail
		// see realLogout method
	}

	public get token() {
		return this.user.get('token');
	}

	public get locale() {
		return 'fr';
	}

	public getRoleUrnArr(): string[] {
		if (this.protoUserRights) {
			return this.protoUserRights.roles.map(r => {
				const urn = r.urn;

				if (!urn) {
					return '';
				}

				return `${urn.partition}:${urn.service}:${urn.resource}:${urn.identifier}`;
			});
		}

		return [];
	}

	public async internalInitialize(options = {}) {
		this._setProcessing(true);

		this.user = new SessionUser();

		this._migrateLegacySession();

		try {
			await this.user.fetch();

			const token = this.user.get('token');
			const refreshToken = this.user.get('refreshToken');

			// La session a pu être ouverte sur une autre application : on revérifie les droits LSD
			if (token && this._isTokenValid(token)) {
				await this.onLoggedIn(
					{ data: { token, refresh_token: refreshToken } },
					WITHOUT_REDIRECTION_TO_COMPANY,
					true,
				);
			} else if (refreshToken) {
				await this._refreshWithToken(refreshToken, true);
			} else {
				this.user.clear();
			}
		} catch (err) {
			// pas de session partagée, ou session invalide / expirée : l'utilisateur passe par le formulaire de login
			this.user.clear();
		}

		this._setProcessing(false);
	}

	@action
	public setIsProcessing(value: boolean) {
		this._processing = value;
	}

	// silent : pas de notification d'erreur (reprise automatique d'une session partagée)
	@action
	protected async onLoggedIn(response, companyRedirection = WITHOUT_REDIRECTION_TO_COMPANY, silent = false) {
		this.user
			.set({
				user_data: jwtDecode(response.data.token),
				token: response.data.token,
				refreshToken: response.data.refresh_token,
			});

		try {
			this._updateProtoUserRights();

			const roleUrnArr = this.getRoleUrnArr();

			// Seul un user lsd peut accéder à LSD
			if (
				!roleUrnArr.includes('jts:lsd:role:ROLE_LSD_USER') &&
				!roleUrnArr.includes('jts:lsd:role:ROLE_LSD_MANAGER') &&
				!roleUrnArr.includes('jts:lsd:role:ROLE_LSD_ADMIN') &&
				!roleUrnArr.includes('jts:lsd:role:ROLE_LSD_SUPER_ADMIN')
			) {
				throw new Error();
			}

			// Enregistré seulement après la vérification des droits LSD
			await this.user.save();

			await this
				.init(companyRedirection)
				.catch(() => {
					// just catch the error when this has not been previously created
				});

			this.setLoggedIn(true);

			Raven.setUserContext({
				id: this.accountModel.id,
				email: this.accountModel.get('email'),
				username: this.accountModel.get('username')
			});
		} catch (err) {
			if (!silent) {
				notification.error({
					description: '',
					message: i18n._('Vous ne disposez pas des autorisations requises pour accéder à cette interface'),
					className: 'app-notification error'
				});
			}

			// Ne touche pas aux cookies partagés : la session reste valide pour les autres applications
			this._clearLocalSession();
		}

		this._refreshing = false;

		return true;
	}

	protected async _refreshWithToken(refreshToken: string, silent = false): Promise<void> {
		const response = await client('/token/refresh', {
			method: 'post',
			data: {
				refresh_token: refreshToken,
				from: 1,
			},
		});

		await this.onLoggedIn(response, WITHOUT_REDIRECTION_TO_COMPANY, silent);
	}

	protected async init(companyRedirection = WITHOUT_REDIRECTION_TO_COMPANY): Promise<any> {
		const promises: Promise<any>[] = [];

		this.accountModel = new UserModel();
		this.accountModel.set({
			id: this.protoUserRights.urn?.identifier
		});
		promises.push(this.accountModel.fetch());

		this.companyCookie = new CompanyCookie();
		this.companyCookie.set({ id: this.accountModel.id });

		promises.push(
			this.companyCookie
				.fetch()
				.then(() => {
					if (companyRedirection && this.companyCookie.get('company')) {
						window.location.href = `/companies/${this.companyCookie.get('company')}/my-tasks`;
					}

					return true;
				})
				.catch(() => {
					// just catch error if not found
				})
		);

		this.lsdGroups = new ApiCollection(GroupLsdModel);
		promises.push(this.lsdGroups.list());

		this.me = new MeModel();
		promises.push(
			this.me
				.fetch()
				.then(() => {
					if (this.me.id) {
						store.appStore.loadAllCurrentUserAndMenuData(this.me.id);
					}

					return true;
				})
		);

		// Récupération du token mercure
		promises.push(
			new MercureTokenModel()
				.fetch()
				.then(mercureModel => {
					currentModelXMercureService.setToken(mercureModel.get('token'));
					return true;
				})
		);

		return Promise.all(promises);
	}

	@action
	private _setProcessing(value: boolean) {
		this._processing = value;
	}

	@action
	private setLoggedIn(value: boolean) {
		this._loggedIn = value;
	}

	private _isTokenValid(token: string): boolean {
		try {
			return ((jwtDecode(token) as { exp?: number }).exp || 0) > Math.ceil(new Date().valueOf() / 1000);
		} catch (err) {
			return false;
		}
	}

	// Reprend une session ouverte avant le passage aux cookies, puis nettoie le localStorage
	private _migrateLegacySession() {
		try {
			const legacySession = JSON.parse(localStorage.getItem(LEGACY_SESSION_USER_KEY) || '{}');

			if (
				legacySession.token
				&& !getSharedItem(SHARED_TOKEN_KEY)
				&& !getSharedItem(SHARED_REFRESH_TOKEN_KEY)
			) {
				setSharedItem(SHARED_TOKEN_KEY, legacySession.token);

				if (legacySession.refreshToken) {
					setSharedItem(SHARED_REFRESH_TOKEN_KEY, legacySession.refreshToken);
				}
			}
		} catch (err) {
			// ancienne session illisible : ignorée
		}

		localStorage.removeItem(LEGACY_SESSION_USER_KEY);
	}

	// Ne touche pas aux cookies partagés : la session reste valide pour les autres applications
	private _clearLocalSession() {
		this.clear();

		store.appStore.clearStore();
		store.organigramStore.clearStore();

		this.setLoggedIn(false);
		this.setIsProcessing(false);

		this.user.clear();

		this.protoUserRights = new UserRights();

		this.accountModel.clear();
		this.me.clear();
		this.lsdGroups.clear();

		Raven.setUserContext({});
	}

	private _updateProtoUserRights() {
		// this.protoUserRights = UserRights.deserializeBinary(
		this.protoUserRights = UserRights.decode(
			new TextEncoder().encode(this.user.get(['user_data', 'platform.token.owner']))
		);
	}
}
